import React, { forwardRef, useContext, useRef } from "react"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faUserGroup } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../settings/config"
import { notebookContext } from "../providers/notebook.provider"
import SchemaDrawing from "../lesson/schema/SchemaDrawing"
import { MOTION_EASE } from "../lesson/schema/schemaMotion"
import {
	exercisePhaseLabel,
	isMine,
	isSharedIn,
	lastUseLabel,
	ownerLabel,
	shortName,
} from "../utils/notebook.utils"

// Gli esercizi del quaderno in una griglia di carte, con lo schema per
// copertina: un esercizio si riconosce prima ancora di leggerne il nome.
// La copertina è la testa della carta, blu fino ai bordi e senza la cornice
// verde: la stessa cosa che si trova in alto aprendola, così la carta si
// allarga nella voce senza cambiare faccia. Sotto, il nome sul fondo caldo,
// come ogni blocco dentro il foglio bianco.
//
// Chi lo schema non ce l'ha ha lo stesso campo, vuoto e sfocato, con
// scritto "Senza schema": niente +, che in basso c'è già quello per una
// voce nuova, e due + arancioni si confonderebbero. Lo schema si aggiunge
// dalla voce aperta.

const c = theme.colors

// Il passaggio fra galleria e lista: ogni carta, la sua copertina e il suo
// nome hanno un nome loro (`layoutId`), uguale nelle due viste, e passando
// dall'una all'altra vanno da dov'erano a dove vanno invece di ricomparire
export const SWAP = { duration: 0.45, ease: MOTION_EASE }
export const swapID = (entry, part) => `notebook-${part}-${entry.entryID}`

// Quello che c'è in una vista e non nell'altra (il testo nella lista, la
// fase sotto il nome in galleria, il campo sfocato di chi non ha schema)
// non vola: arriva piano quando le carte sono quasi ferme
export const ARRIVE = {
	initial: { opacity: 0 },
	animate: { opacity: 1 },
	transition: { delay: 0.22, duration: 0.25 },
}

// Il campo vuoto, sfocato: lo stesso posto dello schema, per dire che
// manca. Lo usa anche la voce aperta, sul blu e senza il verde.
export const EmptyCourt = ({ grass = true }) => (
	<span
		aria-hidden="true"
		style={{ display: "block", width: "100%", height: "100%", filter: "blur(3.5px) saturate(0.7)", opacity: 0.85 }}>
		<SchemaDrawing schema={null} label="" grass={grass} />
	</span>
)

// Un'etichetta su vetro, sopra il campo
export const GlassChip = ({ children, style }) => (
	<span
		style={{
			display: "inline-flex",
			alignItems: "center",
			gap: "0.375rem",
			height: "1.75rem",
			padding: "0 0.75rem",
			borderRadius: "14px",
			background: theme.colors.background_blur,
			backdropFilter: "blur(8px)",
			WebkitBackdropFilter: "blur(8px)",
			color: theme.colors.gunmetal,
			fontSize: "0.75rem",
			fontWeight: 700,
			whiteSpace: "nowrap",
			...style,
		}}>
		{children}
	</span>
)

// La copertina: lo schema sul suo blu, senza la cornice, con la forma del
// campo. In galleria è la testa della carta, nella lista la miniatura; gli
// angoli li dice chi la mette (`radius`, in px, da quello in alto a sinistra
// in giro), e la forma è la stessa, così passando dall'una all'altra si
// ingrandisce senza deformarsi.
export const SchemaCover = forwardRef(function SchemaCover({ entry, radius, width = "100%", empty, badge }, ref) {
	const [tl, tr, br, bl] = radius
	return (
		<motion.span
			ref={ref}
			layoutId={swapID(entry, "cover")}
			transition={SWAP}
			// nella lista chi non ha schema non ha miniatura: in galleria il
			// campo sfocato non arriva da nessuna parte, compare
			{...(entry.schema ? {} : { initial: ARRIVE.initial, animate: ARRIVE.animate })}
			style={{
				position: "relative",
				display: "block",
				flex: "none",
				width,
				aspectRatio: "358 / 400",
				overflow: "hidden",
				borderTopLeftRadius: tl,
				borderTopRightRadius: tr,
				borderBottomRightRadius: br,
				borderBottomLeftRadius: bl,
				background: c.schema_court,
			}}>
			{entry.schema ? (
				<SchemaDrawing schema={entry.schema} grass={false} label={`Schema: ${entry.title}`} />
			) : (
				<>
					<EmptyCourt grass={false} />
					{empty && (
						<span
							style={{
								position: "absolute",
								inset: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							{empty}
						</span>
					)}
				</>
			)}
			{badge}
		</motion.span>
	)
})

// Di chi è, sulla copertina, nell'angolo: "Condiviso" con le due sagome se
// è proprio e condiviso, il nome del collega se è suo. Su vetro, come
// "Senza schema": in galleria si guardano le copertine, e il segno si vede
// senza leggere. Uno solo proprio non ha niente.
const OwnerChip = ({ entry }) => {
	const { schoolID } = useContext(notebookContext) ?? {}
	if (isMine(entry) && !isSharedIn(entry, schoolID)) return null
	return (
		<GlassChip style={{ position: "absolute", top: "0.5rem", left: "0.5rem" }}>
			<FontAwesomeIcon icon={isMine(entry) ? faUserGroup : faUser} />
			{isMine(entry) ? "Condiviso" : shortName(entry.authorName) || "Di un collega"}
		</GlassChip>
	)
}

// La testa della carta è tonda sopra e dritta sotto, dove comincia il nome
export const GALLERY_COVER_RADIUS = [15, 15, 0, 0]

// `onOpen` riceve dove stanno la carta e la copertina, misurate quando
// servono: la voce si apre da lì e ci torna
function GalleryCard({ entry, onOpen }) {
	const card = useRef(null)
	const cover = useRef(null)
	const where = () => ({
		card: card.current?.getBoundingClientRect(),
		cover: cover.current?.getBoundingClientRect(),
		coverRadius: GALLERY_COVER_RADIUS,
	})
	return (
		<motion.button
			ref={card}
			layoutId={swapID(entry, "card")}
			transition={SWAP}
			type="button"
			whileTap={{ scale: 0.97 }}
			onClick={() => onOpen(where)}
			style={{
				display: "flex",
				flexDirection: "column",
				minWidth: 0,
				padding: 0,
				border: 0,
				borderRadius: 15,
				overflow: "hidden",
				background: c.background,
				color: c.gunmetal,
				font: "inherit",
				textAlign: "left",
				cursor: "pointer",
			}}>
			<SchemaCover
				ref={cover}
				entry={entry}
				radius={GALLERY_COVER_RADIUS}
				empty={<GlassChip>Senza schema</GlassChip>}
				badge={<OwnerChip entry={entry} />}
			/>
			<motion.span
				layout="position"
				transition={SWAP}
				style={{ display: "flex", flexDirection: "column", gap: "0.25rem", padding: "0.5rem 0.625rem 0.75rem" }}>
				<motion.span
					layoutId={swapID(entry, "title")}
					layout="position"
					transition={SWAP}
					style={{
						fontWeight: 700,
						fontSize: "0.8125rem",
						lineHeight: 1.25,
						display: "-webkit-box",
						WebkitLineClamp: 2,
						WebkitBoxOrient: "vertical",
						overflow: "hidden",
					}}>
					{entry.title}
				</motion.span>
				<motion.span {...ARRIVE} style={{ fontSize: "0.6875rem", lineHeight: 1.3, color: c.text_light }}>
					{exercisePhaseLabel(entry.phase)} · {isMine(entry) ? lastUseLabel(entry) : ownerLabel(entry)}
				</motion.span>
			</motion.span>
		</motion.button>
	)
}

// `divided`: il filo che le separa dalle pastiglie, se ci sono
function NotebookGallery({ entries, onOpen, divided }) {
	return (
		<div
			style={{
				display: "grid",
				gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
				gap: "0.625rem",
				padding: "0.75rem 0.75rem 0.25rem",
				boxShadow: divided ? `inset 0 1px 0 ${c.gunmetal_lighter}` : "none",
			}}>
			{entries.map(entry => (
				<GalleryCard key={entry.entryID} entry={entry} onOpen={where => onOpen(entry.entryID, where)} />
			))}
		</div>
	)
}

export default NotebookGallery
